import { AssetTypes } from "constant/AssetTypes";
import { decodeHtmlEntities } from "constant/htmlEntities";
import { RouteName } from "constant/routeNames";
import { BsFillPlayFill } from "react-icons/bs";
import { CgMediaLive } from "react-icons/cg";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

function HeroSliderLeft({ data }: any) {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const ppvEnalbed = localStorage.getItem("ppv_enabled");

  const channelClicked = async () => {
    navigate(RouteName.LiveTVPlayerLinkWithSlug.replace(":slug", data.slug));
  };

  const movieClicked = async () => {
    navigate(RouteName.WatchMovies.replace(":slug", data.slug));
  };

  const radioClicked = async () => {
    navigate(RouteName.RadioDescriptionRoute.replace(":slug", data.slug));
  };

  const SeriesClicked = async () => {
    if (data?.first_episode) {
      navigate(
        RouteName.WatchTVSeries.replace(":slug", data.slug).replace(
          ":stream",
          data?.first_episode?.stream_key
        )
      );
    } else {
      navigate(RouteName.Welcome);
    }
  };

  const PodcastClicked = async () => {
    navigate(RouteName.PodcastOpenEpisodeRoute.replace(":id", data.podcast_id));
  };

  const musicClicked = async () => {
    navigate(RouteName.MusicTrackRoute.replace(":id", data.song_id));
  };

  const payperviewClicked = async () => {
    navigate(RouteName.PayperviewDetailsRoute.replace(":slug", data.slug));
  };

  const clean = (value: string) => decodeHtmlEntities(value);

  // shared modern CTA button styling (primary)
  const btnClass =
    "group flex h-[48px] w-[180px] items-center justify-center rounded-full bg-white font-semibold text-black shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.03] hover:shadow-xl hover:shadow-white/30 max-sm:w-[160px]";

  return (
    <div className="hero-slide relative flex h-[68vh] w-full overflow-hidden rounded-2xl max-sm:h-[60vh]">
      {/* scoped animations */}
      <style>{`
        @keyframes heroKenBurns {
          from { transform: scale(1); }
          to { transform: scale(1.12); }
        }
        @keyframes heroFadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .hero-bg {
          animation: heroKenBurns 18s ease-out forwards;
        }
        .hero-content > * {
          opacity: 0;
          animation: heroFadeUp 0.7s ease-out forwards;
        }
        .hero-content > *:nth-child(1) { animation-delay: 0.05s; }
        .hero-content > *:nth-child(2) { animation-delay: 0.15s; }
        .hero-content > *:nth-child(3) { animation-delay: 0.25s; }
        .hero-content > *:nth-child(4) { animation-delay: 0.35s; }
        .hero-content > *:nth-child(5) { animation-delay: 0.45s; }
        @media (prefers-reduced-motion: reduce) {
          .hero-bg, .hero-content > * { animation: none; opacity: 1; }
        }
      `}</style>

      {/* Backdrop with slow Ken Burns zoom */}
      <div
        className="hero-bg absolute inset-0"
        style={{
          backgroundImage: `url(${data.banner})`,
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
        }}
      />

      {/* Content — lower-third left */}
      {data.type !== AssetTypes.Other && (
      <div className="relative z-10 flex h-full w-full items-end">
        <div className="hero-content flex w-full max-w-[640px] flex-col items-start gap-4 p-12 max-sm:max-w-full max-sm:gap-3 max-sm:p-5">
          {/* Eyebrow / caption */}
          {data.caption ? (
            <p className="inline-flex items-center rounded-full bg-[#F50537] px-3.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white shadow-lg shadow-[#F50537]/30">
              {clean(data.caption)}
            </p>
          ) : (
            <></>
          )}

          {/* Title */}
          <h2 className="font-rubik text-[52px] font-extrabold leading-[1.02] tracking-tight text-white drop-shadow-2xl max-sm:text-[28px]">
            {clean(data.title)}
          </h2>

          {/* Metadata row */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-white/80">
            {data.type ? <span className="text-white">{data.type}</span> : null}
            {data.date ? (
              <>
                <span className="h-1 w-1 rounded-full bg-white/40" />
                <span>{data.date}</span>
              </>
            ) : null}
          </div>

          {/* Synopsis */}
          <p className="line-clamp-2 max-w-xl text-[15px] leading-relaxed text-white/80 max-sm:text-[13px]">
            {clean(data.description)}
          </p>

          {/* Actions */}
          <div className="mt-2 flex items-center gap-3">
            {data.type === "Live TV" ? (
              <button className={btnClass}>
                <p
                  className="flex items-center justify-center"
                  onClick={channelClicked}
                >
                  <CgMediaLive className="stream-icon-hero" />
                  <span className="ml-2 text-black">{data.cta}</span>
                </p>
              </button>
            ) : data.type === AssetTypes.TVSeries ? (
              <button className={btnClass}>
                <p
                  className="flex items-center justify-center"
                  onClick={SeriesClicked}
                >
                  <BsFillPlayFill className="stream-icon-hero" />
                  <span className="ml-2 text-black">{data.cta}</span>
                </p>
              </button>
            ) : data.type === AssetTypes.Podcast ? (
              <button className={btnClass}>
                <p
                  className="flex items-center justify-center"
                  onClick={PodcastClicked}
                >
                  <BsFillPlayFill className="stream-icon-hero" />
                  <span className="ml-2 text-black">{data.cta}</span>
                </p>
              </button>
            ) : data.type === AssetTypes.Music ? (
              <button className={btnClass}>
                <p
                  className="flex items-center justify-center"
                  onClick={musicClicked}
                >
                  <BsFillPlayFill className="stream-icon-hero" />
                  <span className="ml-2 text-black">{data.cta}</span>
                </p>
              </button>
            ) : data.type === AssetTypes.Movie ? (
              <button className={btnClass}>
                <p
                  className="flex items-center justify-center"
                  onClick={movieClicked}
                >
                  <BsFillPlayFill className="stream-icon-hero" />
                  <span className="ml-2 text-black">{data.cta}</span>
                </p>
              </button>
            ) : data.type === AssetTypes.Radio ? (
              <button className={btnClass}>
                <p
                  className="flex items-center justify-center"
                  onClick={radioClicked}
                >
                  <BsFillPlayFill className="stream-icon-hero" />
                  <span className="ml-2 text-black">{data.cta}</span>
                </p>
              </button>
            ) : data.type === AssetTypes.PayPerView ? (
              <button className={btnClass}>
                {ppvEnalbed === "1" ? (
                  <p
                    className="flex items-center justify-center"
                    onClick={payperviewClicked}
                  >
                    <BsFillPlayFill className="stream-icon-hero" />
                    <span className="ml-2 text-black">{data.cta}</span>
                  </p>
                ) : (
                  <p className="flex items-center justify-center">
                    <span className="ml-2 text-black">Coming Soon</span>
                  </p>
                )}
              </button>
            ) : (
              <>
                {data.is_coming_soon === 0 && (
                  <button className={btnClass}>
                    <a
                      href={data.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-center"
                    >
                      <BsFillPlayFill className="stream-icon-hero" />
                      <span className="ml-2 text-black">{data.cta}</span>
                    </a>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

export default HeroSliderLeft;